import React from 'react';
import { css } from '@emotion/css';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Collapse,
  CircularProgress,
  Tooltip,
  useTheme,
  Theme,
} from '@mui/material';
import { BiCheckCircle, BiErrorCircle, BiTime, BiCloudUpload } from 'react-icons/bi';
import { useCSVReader } from 'react-papaparse';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';
import { ModalHeader, ModalContent, ModalFooter, ModalAction } from '@components/ModalComponents';
import useDriverImport, { ImportRow, RowStatus } from '@features/drivers/hooks/useDriverImport';

interface ImportDriversModalContentProps {
  onClose: (didImport?: boolean) => void;
}

const StatusIcon = ({ status, error }: { status: RowStatus; error?: string }) => {
  switch (status) {
    case 'pending':
      return <BiTime size={18} color='gray' />;
    case 'processing':
      return <CircularProgress size={16} />;
    case 'success':
      return <BiCheckCircle size={18} color='green' />;
    case 'error':
      return (
        <Tooltip title={error || 'Error'} arrow>
          <span style={{ display: 'inline-flex' }}>
            <BiErrorCircle size={18} color='red' />
          </span>
        </Tooltip>
      );
  }
};

const ImportDriversModalContent = ({ onClose }: ImportDriversModalContentProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const { CSVReader } = useCSVReader();
  const { rows, isProcessing, isComplete, parseCsv, processAll, reset, validCount, successCount, errorCount } =
    useDriverImport();

  const [expandedRow, setExpandedRow] = React.useState<number | null>(null);
  const hasRows = rows.length > 0;

  // Hold a reload while uploaded rows wait to be imported, and while the import runs.
  const hasPendingImport = isProcessing || (hasRows && !isComplete);
  useReloadGuard(hasPendingImport, 'Driver import');

  const handleClose = () => {
    reset();
    onClose(isComplete && successCount > 0);
  };

  const handleImport = () => {
    processAll();
  };

  return (
    <>
      <ModalHeader handleClose={handleClose}>Import Drivers</ModalHeader>

      <ModalContent>
        {!hasRows ? (
          <CSVReader
            onUploadAccepted={(results: any) => parseCsv(results)}
            onDragOver={(e: any) => e.preventDefault()}
            onDragLeave={(e: any) => e.preventDefault()}
            config={{ skipEmptyLines: true }}
          >
            {({ getRootProps }: any) => (
              <div className={cls.uploadZone}>
                <Button
                  fullWidth
                  variant='outlined'
                  sx={{ minHeight: 120, borderStyle: 'dashed', fontSize: 16 }}
                  {...getRootProps()}
                >
                  <BiCloudUpload size={24} style={{ marginRight: 8 }} />
                  Click or Drag CSV File to Upload
                </Button>

                <div className={cls.templateLink}>
                  <a href='/downloads/hopdrive_driver_import.csv' download>
                    <Button size='small' variant='text'>
                      Download CSV Template
                    </Button>
                  </a>
                </div>

                <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
                  CSV should have columns: name, email, phone
                </Typography>
              </div>
            )}
          </CSVReader>
        ) : (
          <>
            <Typography variant='body2' color='textSecondary' sx={{ mb: 1 }}>
              {rows.length} row{rows.length !== 1 ? 's' : ''} found
              {!isComplete && ` — ${validCount} valid, ${errorCount} with errors`}
              {isComplete && ` — ${successCount} created, ${errorCount} failed`}
            </Typography>

            <TableContainer className={cls.tableContainer}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={40}>#</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell width={60} align='center'>
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row: ImportRow) => (
                    <React.Fragment key={row.index}>
                      <TableRow
                        hover
                        onClick={() => row.error && setExpandedRow(expandedRow === row.index ? null : row.index)}
                        sx={{ cursor: row.error ? 'pointer' : 'default' }}
                      >
                        <TableCell>{row.index + 1}</TableCell>
                        <TableCell>{row.name || '—'}</TableCell>
                        <TableCell>{row.email || '—'}</TableCell>
                        <TableCell>{row.phone || '—'}</TableCell>
                        <TableCell align='center'>
                          <StatusIcon status={row.status} error={row.error} />
                        </TableCell>
                      </TableRow>
                      {row.error && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ py: 0, borderBottom: expandedRow === row.index ? undefined : 'none' }}>
                            <Collapse in={expandedRow === row.index}>
                              <Typography variant='body2' color='error' sx={{ py: 1 }}>
                                {row.error}
                              </Typography>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </ModalContent>

      <ModalFooter>
        {!isComplete ? (
          <>
            <ModalAction disabled={!hasRows || validCount === 0 || isProcessing} loading={isProcessing} onClick={handleImport}>
              Import Drivers
            </ModalAction>
            <ModalAction color='utility' disabled={isProcessing} onClick={handleClose}>
              Cancel
            </ModalAction>
          </>
        ) : (
          <ModalAction onClick={handleClose}>Done</ModalAction>
        )}
      </ModalFooter>
    </>
  );
};

const useStyles = (theme?: Theme) => ({
  uploadZone: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: ${theme?.spacing(2)};
  `,
  templateLink: css`
    margin-top: ${theme?.spacing(1)};
  `,
  tableContainer: css`
    max-height: 400px;
    overflow: auto;
  `,
});

export default ImportDriversModalContent;
